import { useCallback, useContext, useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import useAuth from 'CustomHooks/useAuth';
import { RestaurantContext } from 'Contexts/RestaurantContext';

// A call that waited longer than this is dropped: by then the user is looking
// at something else.
export const MAX_WAIT = 3000;

// Roles and PIN settings arrive after the page is already clickable. A call in
// that gap waits for them instead of being answered with "no permission".
const useAfterRolesLoaded = <A extends any[]>(
  handler: (...args: A) => void
) => {
  const { rolesLoaded, restaurantId } = useAuth();
  const { securityPlusLoaded } = useContext(RestaurantContext);
  const ready = rolesLoaded && securityPlusLoaded;

  // Read through a ref, so a wrapper kept from an earlier render does not act
  // on that render's state.
  const current = useRef({ handler, ready, restaurantId });
  current.current = { handler, ready, restaurantId };

  const pending = useRef<null | {
    args: A;
    at: number;
    restaurantId: string | null;
  }>(null);

  useEffect(() => {
    const call = pending.current;

    if (!ready || !call) return;

    pending.current = null;

    if (
      call.restaurantId !== current.current.restaurantId ||
      Date.now() - call.at > MAX_WAIT
    ) {
      return;
    }

    try {
      current.current.handler(...call.args);
    } catch (err) {
      // Thrown from an effect it would unmount the whole app, in a click
      // handler it is only a console error.
      console.error(err);
      Sentry.captureException(err);
    }
  }, [ready]);

  return useCallback((...args: A) => {
    if (!current.current.ready) {
      pending.current = {
        args,
        at: Date.now(),
        restaurantId: current.current.restaurantId,
      };
      return;
    }

    current.current.handler(...args);
  }, []);
};

export default useAfterRolesLoaded;
